import { useState, useEffect } from 'react'
import { useModal } from '@/hooks/modal'
import {
  CompanyContactPerson,
  CompanyData,
  CompanyMemo,
  Status,
  UsersResult,
} from 'types/api'
import { CompanySummary } from './molecules/domestic/CompanySummary'
import { CompanyTabGroup } from './molecules/domestic/CompanyTabGroup'
import { TrialModal } from './molecules/TrialModal'
import { SourcingAnnotationPanel } from '../companies/components/molucule/SourcingAnnotationPanel'
import { Cover } from '@/components/common/Cover'
import {
  addMyLabel,
  deleteCompanyContactPerson,
  deleteCompanyStatus,
  deleteFirstInterviewDate,
  getCompanyStatuses,
  getContactPersons,
  registerCompanyContactPerson,
  registerCompanyStatus,
  registerFirstInterviewDate,
  removeMyLabel,
  saveCompanyMemo,
} from '@/lib/api/company'
import {
  deleteCompanySourcingRoute,
  postSourcingRoute,
  putSourcingRoute,
  registerCompanySourcingRoute,
  deleteSourcingRoute,
  getSourcingAnnotationCandidates,
  postMyLabel,
  putMyLabel,
  deleteMyLabel,
} from '@/lib/api/status'
import { CompanyDownloadModal } from './molecules/domestic/CompanyDownloadModal'
import { isEnterpriseWithTrial, isGuest } from '@/lib/user'
import { CompanyTabType, User } from 'types'
import { css } from '@emotion/react'
import {
  MyLabelId,
  SourcingRoute,
} from '../companies/reducers/sourcingAnnotationCandidates'
import { SourcingAnnotationCandidateEditModal } from '../companies/components/SourcingAnnotationCandidateEditModal'
import { isExited, isResearchDone } from '@/lib/company'

type Props = {
  company: Required<CompanyData>
  status: Status | null
  memo: CompanyMemo | null
  contactPerson: CompanyContactPerson | null
  activeTab: CompanyTabType | null
  user: User
}

export const DomesticCompanyHeader: React.FC<Props> = ({
  company,
  status,
  memo,
  contactPerson,
  activeTab,
  user,
}) => {
  const [isStatusPanelVisible, setIsStatusPanelVisible] = useState(false)
  const [isHiddenMyLabelCandidateModal, setIsHiddenMyLabelCandidateModal] =
    useState(true)
  const [
    isHiddenSourcingRouteCandidateModal,
    setIsHiddenSourcingRouteCandidateModal,
  ] = useState(true)
  const [statuses, setStatuses] = useState<Status[]>([])
  const [contactPersons, setContactPersons] = useState<UsersResult>({
    users: [],
  })
  const [myLabels, setMyLabels] = useState<{ id: number; name: string }[]>([])
  const [sourcingRoutes, setSourcingRoutes] = useState<SourcingRoute[]>([])
  const [selectedStatus, setSelectedStatus] = useState<Status | null>(status)
  const [selectedContactPerson, setSelectedContactPerson] =
    useState<CompanyContactPerson | null>(contactPerson)

  // 表示中の企業に依存しない情報の取得
  useEffect(() => {
    const run = async () => {
      const [statuses, contactPersons, candidates] = await Promise.all([
        getCompanyStatuses(),
        getContactPersons(),
        getSourcingAnnotationCandidates(),
      ])
      setStatuses(statuses)
      setContactPersons(contactPersons)
      if (candidates) {
        setMyLabels(candidates.myLabels)
        setSourcingRoutes(candidates.sourcingRoutes)
      }
    }
    run()
  }, [])

  // NOTE: 別の個別企業ページに直接遷移したときに
  // ステータスと担当者の状態を初期化する
  // これがないと、遷移前の企業のステータスや担当者が表示されてしまう
  // refs: https://uzabase.slack.com/archives/C9JE6SZNK/p1701418971486939
  useEffect(() => {
    setSelectedStatus(status)
    setSelectedContactPerson(contactPerson)
  }, [company.shortname])

  const {
    isOpen: isTrialModalOpen,
    show: showTrialModal,
    hide: hideTrialModal,
  } = useModal({ key: 'trial' })

  const {
    isOpen: isDownloadModalOpen,
    show: showDownloadModal,
    hide: hideDownloadModal,
  } = useModal({ key: 'download' })

  const refreshSourcingAnnotationCandidates = async () => {
    const candidates = await getSourcingAnnotationCandidates()
    if (candidates) {
      setMyLabels(candidates.myLabels)
      setSourcingRoutes(candidates.sourcingRoutes)
    }
  }

  const handleSourcingRouteModalClose = () => {
    setIsHiddenSourcingRouteCandidateModal(true)
  }

  const handleSelectSourcingRoute = async (
    companyId: string,
    sourcingRouteId: number,
    _sourcingRouteName: string,
    _user: User
  ) => {
    await registerCompanySourcingRoute(companyId, sourcingRouteId)
  }

  const handleDeselectSourcingRoute = async (companyId: string) => {
    await deleteCompanySourcingRoute(companyId)
  }

  const handleRequestEditSourcingRouteModal = () => {
    setIsHiddenSourcingRouteCandidateModal(false)
  }

  const handleCreateSourcingRoute = async (name: string) => {
    await postSourcingRoute(name)
    await refreshSourcingAnnotationCandidates()
  }

  const handleUpdateSourcingRoute = async (id: number, name: string) => {
    await putSourcingRoute(id, name)
    await refreshSourcingAnnotationCandidates()
  }

  const handleDeleteSourcingRoute = async (id: number) => {
    await deleteSourcingRoute(id)
    await refreshSourcingAnnotationCandidates()
  }

  const handleRequestEditMyLabelModal = () => {
    setIsHiddenMyLabelCandidateModal(false)
  }

  const handleMyLabelModalClose = () => {
    setIsHiddenMyLabelCandidateModal(true)
  }

  const handleCreateMyLabel = async (name: string) => {
    await postMyLabel(name)
    await refreshSourcingAnnotationCandidates()
  }

  const handleUpdateMyLabel = async (id: MyLabelId, name: string) => {
    await putMyLabel(id, name)
    await refreshSourcingAnnotationCandidates()
  }

  const handleDeleteMyLabel = async (id: MyLabelId) => {
    await deleteMyLabel(id)
    await refreshSourcingAnnotationCandidates()
  }

  return (
    <>
      {isStatusPanelVisible && (
        <Cover zIndex={21} handleClick={() => setIsStatusPanelVisible(false)} />
      )}
      <div
        data-test-id="domestic-company-header"
        data-testid="domestic-company-header"
        css={css`
          display: inline;
        `}
      >
        <CompanySummary
          company={company}
          showModal={showTrialModal}
          setIsStatusPanelVisible={setIsStatusPanelVisible}
          companyStatus={status}
          contactPerson={contactPerson}
          statuses={statuses}
          users={contactPersons}
          companyMemo={memo}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          selectedContactPerson={selectedContactPerson}
          setSelectedContactPerson={setSelectedContactPerson}
        />
        {!(isGuest(user) && isResearchDone(company) && !isExited(company)) && (
          <CompanyTabGroup
            data={company}
            activeTab={activeTab}
            showDownloadModal={showDownloadModal}
            showTrialModal={showTrialModal}
          />
        )}
      </div>
      <SourcingAnnotationPanel
        isOpen={isStatusPanelVisible}
        myLabels={myLabels}
        sourcingRoutes={sourcingRoutes}
        companyShortname={company.shortname}
        onSelectStatus={async (companyId, status) => {
          await registerCompanyStatus(companyId, status.id)
          setSelectedStatus(status)
        }}
        onSelectStatusBlank={async (companyId) => {
          await deleteCompanyStatus(companyId)
          setSelectedStatus(null)
        }}
        onSelectContactPerson={async (contactPerson) => {
          await registerCompanyContactPerson(
            contactPerson.companyId,
            contactPerson.userId,
            () => {}
          )
          setSelectedContactPerson(contactPerson)
        }}
        onSelectContactPersonBlank={async (companyId) => {
          await deleteCompanyContactPerson(companyId, () => {})
          setSelectedContactPerson(null)
        }}
        onSelectMyLabel={(myLabelId, companyId) => {
          addMyLabel(companyId, myLabelId, user)
        }}
        onDeselectMyLabel={(myLabelId, companyId) => {
          removeMyLabel(companyId, myLabelId, user)
        }}
        onRequestEditMyLabelModal={handleRequestEditMyLabelModal}
        onSelectFirstInterviewDate={async (companyId, firstInterviewDate) => {
          await registerFirstInterviewDate(companyId, firstInterviewDate)
        }}
        onSelectFirstInterviewDateBlank={async (companyId) => {
          await deleteFirstInterviewDate(companyId)
        }}
        onSelectSourcingRoute={handleSelectSourcingRoute}
        onDeselectSourcingRoute={handleDeselectSourcingRoute}
        onRequestEditSourcingRouteModal={handleRequestEditSourcingRouteModal}
        onUpdateMemo={async (companyId, memo) => {
          await saveCompanyMemo(companyId, memo, () => {})
        }}
        closePanel={() => {
          setIsStatusPanelVisible(false)
        }}
      />
      <TrialModal isOpen={isTrialModalOpen} hideModal={hideTrialModal} />
      {isEnterpriseWithTrial(user) && (
        <CompanyDownloadModal
          shortname={company.shortname}
          name={company.name}
          isOpen={isDownloadModalOpen}
          onClose={hideDownloadModal}
        />
      )}
      <SourcingAnnotationCandidateEditModal
        isHidden={isHiddenSourcingRouteCandidateModal}
        modalId="sourcing-route"
        modalName="ソーシングルート"
        onCreate={handleCreateSourcingRoute}
        onUpdate={handleUpdateSourcingRoute}
        onDelete={handleDeleteSourcingRoute}
        onClose={handleSourcingRouteModalClose}
      >
        {sourcingRoutes}
      </SourcingAnnotationCandidateEditModal>
      <SourcingAnnotationCandidateEditModal
        isHidden={isHiddenMyLabelCandidateModal}
        modalId="my-label"
        modalName="マイラベル"
        onCreate={handleCreateMyLabel}
        onUpdate={handleUpdateMyLabel}
        onDelete={handleDeleteMyLabel}
        onClose={handleMyLabelModalClose}
      >
        {myLabels}
      </SourcingAnnotationCandidateEditModal>
    </>
  )
}
